import React from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigProvider } from 'antd';
import App from './App';
import { lightTheme } from './theme/antdTheme';
import './styles/global.css';
import './styles/dashboard-premium.css';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('React Error:', error, errorInfo);
        this.setState({ errorInfo });

        // After webpack restarts, browsers often keep an old chunk hash.
        // Reload once so the app picks up the new LoginPage (and other) chunks.
        const isChunkError =
            error?.name === 'ChunkLoadError'
            || /Loading chunk [\w/-]+ failed/i.test(String(error?.message || error));

        if (isChunkError) {
            const reloadKey = 'upos_chunk_reload';
            if (!sessionStorage.getItem(reloadKey)) {
                sessionStorage.setItem(reloadKey, '1');
                window.location.reload();
                return;
            }
            sessionStorage.removeItem(reloadKey);
        }
    }

    render() {
        if (this.state.hasError) {
            const isChunkError =
                this.state.error?.name === 'ChunkLoadError'
                || /Loading chunk [\w/-]+ failed/i.test(String(this.state.error?.message || this.state.error));

            return (
                <div style={{
                    padding: 40,
                    background: '#F5F7FB',
                    color: '#374151',
                    minHeight: '100vh',
                    fontFamily: 'Inter, sans-serif',
                }}>
                    <h1 style={{ color: '#EF4444' }}>Something went wrong</h1>
                    {isChunkError ? (
                        <div style={{
                            background: '#FFFFFF',
                            padding: 20,
                            borderRadius: 8,
                            border: '1px solid #E5E7EB',
                            maxWidth: 520,
                        }}>
                            <p style={{ marginBottom: 16 }}>
                                The app was updated while this page was open. Reload to continue.
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    sessionStorage.removeItem('upos_chunk_reload');
                                    window.location.reload();
                                }}
                                style={{
                                    background: '#2563EB',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 8,
                                    padding: '10px 18px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Reload app
                            </button>
                        </div>
                    ) : (
                        <pre style={{
                            background: '#FFFFFF',
                            padding: 20,
                            borderRadius: 8,
                            overflow: 'auto',
                            color: '#374151',
                            border: '1px solid #E5E7EB',
                        }}>
                            {this.state.error?.toString()}
                            {this.state.errorInfo?.componentStack}
                        </pre>
                    )}
                </div>
            );
        }
        return this.props.children;
    }
}

try {
    const container = document.getElementById('root');

    if (!container) {
        throw new Error('Root container not found');
    }

    const root = createRoot(container);

    root.render(
        <React.StrictMode>
            <ErrorBoundary>
                <ConfigProvider theme={lightTheme}>
                    <App />
                </ConfigProvider>
            </ErrorBoundary>
        </React.StrictMode>
    );

    // Successful boot — allow future chunk-miss reloads again
    sessionStorage.removeItem('upos_chunk_reload');
    sessionStorage.removeItem('upos_lazy_chunk_reload');

} catch (error) {
    console.error('UniversalPOS: Failed to initialize:', error);
    const root = document.getElementById('root');
    if (root) {
        root.innerHTML = `
            <div style="padding: 40px; background: #F5F7FB; color: #374151; min-height: 100vh; font-family: Inter, sans-serif;">
                <h1 style="color: #EF4444;">Failed to Initialize</h1>
                <pre style="background: #FFFFFF; padding: 20px; border-radius: 8px; overflow: auto; border: 1px solid #E5E7EB;">${error.message}\n${error.stack}</pre>
            </div>
        `;
    }
}
